import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtGHS, fmtDateTime, pName } from "@/lib/format";
import { printDoc, esc } from "@/lib/print";
import { FileText, Plus, Trash2, Printer, Send } from "lucide-react";
import { toast } from "sonner";
import { ScanButton, scanOnEnter } from "@/components/ProductCombobox";

export default function Proforma() {
  const { branches, activeBranch } = useBranch();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ branch_id: "", customer_id: "", valid_until: "", notes: "", lines: [] });
  const [q, setQ] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(() => api.get("/proformas").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/customers", { params: { type: "corporate" } }).then((r) => setCustomers(r.data)).catch(() => {});
  }, []);
  useEffect(() => { api.get("/products", { params: { q: q || undefined } }).then((r) => setProducts(r.data.filter((p) => p.is_active))); }, [q]);

  const openNew = () => {
    const b = branches.find((x) => x.id === (activeBranch !== "all" ? activeBranch : branches[0]?.id));
    setForm({ branch_id: b?.id || branches[0]?.id || "", customer_id: "", valid_until: "", notes: "", lines: [] });
    setOpen(true);
  };
  const addLine = (p) => setForm((f) => f.lines.find((l) => l.product_id === p.id)
    ? f : { ...f, lines: [...f.lines, { product_id: p.id, name: pName(p), sku: p.sku, quantity: 1, discount_percent: 0 }] });
  const setLine = (id, k, v) => setForm((f) => ({ ...f, lines: f.lines.map((l) => l.product_id === id ? { ...l, [k]: k === "quantity" ? Math.max(1, Number(v) || 1) : Math.max(0, Number(v) || 0) } : l) }));
  const rmLine = (id) => setForm((f) => ({ ...f, lines: f.lines.filter((l) => l.product_id !== id) }));

  const save = async () => {
    if (!form.branch_id || !form.customer_id) return toast.error("Choose a branch and corporate customer");
    if (form.lines.length === 0) return toast.error("Add at least one item");
    if (!form.valid_until) return toast.error("Enter a valid-until date");
    if (form.valid_until < today) return toast.error("Valid-until date cannot be before today");
    try {
      const { data } = await api.post("/proformas", {
        branch_id: form.branch_id, customer_id: form.customer_id, valid_until: form.valid_until, notes: form.notes,
        lines: form.lines.map((l) => ({ product_id: l.product_id, quantity: l.quantity, discount_percent: l.discount_percent })),
      });
      toast.success(`Proforma ${data.proforma_no} created`);
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const print = async (pf) => {
    const full = (await api.get(`/proformas/${pf.id}`)).data;
    const rowsHtml = full.lines.map((l) => `<tr><td>${esc(l.generic_name)}${l.discount_percent ? ` (${l.discount_percent}% off)` : ""}</td><td class="r">${l.quantity}</td><td class="r">${fmtGHS(l.unit_price)}</td><td class="r">${fmtGHS(l.line_total)}</td></tr>`).join("");
    const body = `<div class="row"><div><img src="${window.location.origin}/mobimed-logo.png" alt="" style="height:46px;margin-bottom:6px"/><h1>MobiMed Pharmacy</h1><h2>PROFORMA INVOICE ${full.proforma_no}</h2><div class="muted">${esc(full.branch_name)}</div></div>
      <div class="muted" style="text-align:right">${fmtDateTime(full.created_at)}<br/>${esc(full.customer_name)}<br/>Corporate quote${full.valid_until ? `<br/>Valid until ${full.valid_until}` : ""}</div></div>
      <table><thead><tr><th>Item</th><th class="r">Qty</th><th class="r">Unit</th><th class="r">Total</th></tr></thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot><tr><td colspan="3" class="r">Subtotal</td><td class="r">${fmtGHS(full.subtotal)}</td></tr>
      <tr><td colspan="3" class="r">Tax</td><td class="r">${fmtGHS(full.tax)}</td></tr>
      <tr><td colspan="3" class="r">Total</td><td class="r">${fmtGHS(full.total)}</td></tr></tfoot></table>
      <div class="muted" style="margin-top:18px">This is a proforma / quotation and is not a demand for payment. ${esc(full.notes)}</div>`;
    printDoc(`Proforma ${full.proforma_no}`, body);
  };

  const convert = async (pf) => {
    try { const { data } = await api.post(`/proformas/${pf.id}/convert`); toast.success(`Converted to ${data.invoice_no}`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Proforma Quotes" subtitle="Corporate quotations — print and later convert into a posted sale"
        actions={<Button data-testid="new-proforma-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1" /> New proforma</Button>} />
      <Card className="shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
            <tr><th className="py-2.5 px-3">Proforma</th><th className="py-2.5 px-3">Customer</th><th className="py-2.5 px-3">Valid until</th><th className="py-2.5 px-3 text-right">Total</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3"></th></tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground"><FileText className="h-8 w-8 mx-auto mb-2 opacity-40" />No proformas yet</td></tr>}
            {rows.map((pf) => (
              <tr key={pf.id} className="border-t border-border hover:bg-muted/50" data-testid={`proforma-row-${pf.proforma_no}`}>
                <td className="py-2 px-3 font-mono text-xs">{pf.proforma_no}</td>
                <td className="py-2 px-3">{pf.customer_name}</td>
                <td className="py-2 px-3 text-xs text-muted-foreground">{pf.valid_until || "—"}</td>
                <td className="py-2 px-3 text-right font-mono">{fmtGHS(pf.total)}</td>
                <td className="py-2 px-3">{pf.status === "converted" ? <StatusBadge value="received" label={`Converted · ${pf.invoice_no}`} /> : <StatusBadge value="requested" label="Open" />}</td>
                <td className="py-2 px-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" data-testid={`print-proforma-${pf.proforma_no}`} onClick={() => print(pf)}><Printer className="h-3.5 w-3.5" /></Button>
                  {pf.status !== "converted" && <Button size="sm" variant="outline" className="ml-1" data-testid={`convert-proforma-${pf.proforma_no}`} onClick={() => convert(pf)}><Send className="h-3.5 w-3.5 mr-1" /> Send to POS</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>New proforma (corporate quote)</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div><Label>Branch</Label><Select value={form.branch_id} onValueChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}><SelectTrigger data-testid="proforma-branch"><SelectValue placeholder="Branch" /></SelectTrigger><SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Corporate customer</Label><Select value={form.customer_id} onValueChange={(v) => setForm((f) => ({ ...f, customer_id: v }))}><SelectTrigger data-testid="proforma-customer"><SelectValue placeholder="Customer" /></SelectTrigger><SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Valid until</Label><Input type="date" min={today} data-testid="proforma-valid" value={form.valid_until} onChange={(e) => setForm((f) => ({ ...f, valid_until: e.target.value }))} /></div>
            </div>
            <div>
              <Label>Add items</Label>
              <div className="flex gap-1 mb-1">
                <Input placeholder="Search name, code or barcode — or scan…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={scanOnEnter(products, (p) => { if (p.is_active === false) return toast.error("Product is inactive"); addLine(p); setQ(""); })} data-testid="proforma-search" />
                <ScanButton products={products} onProduct={(p) => (p.is_active === false ? toast.error("Product is inactive") : addLine(p))} testId="proforma-scan" />
              </div>
              <div className="max-h-28 overflow-y-auto border border-border rounded-md divide-y">
                {products.slice(0, 20).map((p) => (
                  <button type="button" key={p.id} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted" data-testid={`proforma-add-${p.sku}`} onClick={() => addLine(p)}>{pName(p)} · {p.sku}</button>
                ))}
              </div>
            </div>
            <div className="border border-border rounded-md divide-y max-h-48 overflow-y-auto">
              {form.lines.length === 0 && <div className="p-3 text-xs text-muted-foreground">No items added</div>}
              {form.lines.map((l) => (
                <div key={l.product_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`proforma-line-${l.sku}`}>
                  <span className="flex-1 truncate">{l.name}</span>
                  <Input type="number" value={l.quantity} onChange={(e) => setLine(l.product_id, "quantity", e.target.value)} className="h-7 w-16 text-xs" title="Qty" data-testid={`proforma-qty-${l.sku}`} />
                  <button onClick={() => rmLine(l.product_id)} className="text-muted-foreground hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="proforma-notes" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button data-testid="save-proforma-btn" onClick={save}>Create proforma</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
